import React from "react";
import styled from "styled-components";

const TileContainer = styled.article`
  border-radius: 0.5em;
  border: 1px solid rgba(115, 121, 128, 0.1);
  overflow: hidden;
  flex: 1 1 13.4em;
  max-width: 13.4em;
  background-color: #16191b;
`;

const TileContent = styled.div`
  display: flex;
  padding: 1em;
  align-items: center;
  overflow: hidden;
  justify-content: space-between;
`;

const ServerData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-family: Manrope, -apple-system, Roboto, Helvetica, sans-serif;
  white-space: nowrap;
  justify-content: center;
`;

const ServerInfo = styled.div`
  display: flex;
  min-height: 1.5em;
  align-items: center;
  gap: 0.5em;
  justify-content: start;
`;

const ServerNumber = styled.div`
  align-self: stretch;
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  min-height: 1.375em;
  align-items: center;
  font-size: 0.875em;
  color: #fff;
  font-weight: 500;
  letter-spacing: -0.0175em;
  line-height: 1;
  justify-content: start;
`;

const NumberSymbol = styled.span`
  color: #fff;
  align-self: stretch;
  margin-top: auto;
  margin-bottom: auto;
`;

const NumberValue = styled.span`
  color: #fff;
  align-self: stretch;
  margin-top: auto;
  margin-bottom: auto;
`;

const ServerLocation = styled.div`
  align-self: stretch;
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  align-items: center;
  gap: 0.25em;
  font-size: 0.75em;
  color: #fff;
  font-weight: 400;
  line-height: 1;
  justify-content: start;
`;

const LocationContainer = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  gap: 0.25em;
  justify-content: start;
`;

const FlagIcon = styled.img`
  aspect-ratio: 1.6;
  object-fit: contain;
  object-position: center;
  width: 2em;
  align-self: stretch;
  margin-top: auto;
  margin-bottom: auto;
  flex-shrink: 0;
`;

const CountryCode = styled.span`
  align-self: stretch;
  margin-top: auto;
  margin-bottom: auto;
`;

const ServerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3em;
  overflow: hidden;
  justify-content: start;
  width: max-content;
`;

const ControlButton = styled.button`
  align-self: stretch;
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  align-items: center;
  gap: 0.5em;
  overflow: hidden;
  justify-content: start;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.7;
  }
  
  & > svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

const ServerTile = ({ serverNumber = "457", countryCode = "AU", flagSrc, onPlay, onCopy }) => {
  return (
    <TileContainer>
      <TileContent>
        <ServerData>
          <ServerInfo>
            <ServerNumber>
              <NumberSymbol>#</NumberSymbol>
              <NumberValue>{serverNumber}</NumberValue>
            </ServerNumber>
            <ServerLocation>
              <LocationContainer>
                <FlagIcon src={flagSrc} alt={`${countryCode} flag`} />
                <CountryCode>{countryCode}</CountryCode>
              </LocationContainer>
            </ServerLocation>
          </ServerInfo>
        </ServerData>
        <ServerControls>
          <ControlButton onClick={onPlay} aria-label="Play on server">
            <svg width="2em" height="2em" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_1733_12389)">
                <path d="M17.6781 13.3742C18.2938 12.9784 18.2896 12.077 17.6703 11.6869L9.53294 6.56169C8.86702 6.14227 8 6.62085 8 7.40785L8 17.7645C8 18.5557 8.87527 19.0335 9.54078 18.6056L17.6781 13.3742Z" fill="#A1AAB2"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12 24.5962C18.6274 24.5962 24 19.2236 24 12.5962C24 5.96877 18.6274 0.596191 12 0.596191C5.37258 0.596191 0 5.96877 0 12.5962C0 19.2236 5.37258 24.5962 12 24.5962ZM22 12.5962C22 18.119 17.5228 22.5962 12 22.5962C6.47715 22.5962 2 18.119 2 12.5962C2 7.07334 6.47715 2.59619 12 2.59619C17.5228 2.59619 22 7.07334 22 12.5962Z" fill="#A1AAB2"/>
              </g>
              <defs>
                <clipPath id="clip0_1733_12389">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.596191)"/>
                </clipPath>
              </defs>
            </svg>
          </ControlButton>
          <ControlButton onClick={onCopy} aria-label="Copy server address">
            <svg width="2em" height="2em" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7 7.59619V5.59619C7 3.93934 8.34315 2.59619 10 2.59619H19C20.6569 2.59619 22 3.93934 22 5.59619V14.5962C22 16.253 20.6569 17.5962 19 17.5962H17V19.5962C17 21.253 15.6569 22.5962 14 22.5962H5C3.34315 22.5962 2 21.253 2 19.5962V10.5962C2 8.93934 3.34315 7.59619 5 7.59619H7ZM10 4.59619H19C19.5523 4.59619 20 5.04391 20 5.59619V14.5962C20 15.1485 19.5523 15.5962 19 15.5962H17V10.5962C17 8.93934 15.6569 7.59619 14 7.59619H9V5.59619C9 5.04391 9.44771 4.59619 10 4.59619Z" fill="#A1AAB2"/>
            </svg>
          </ControlButton>
        </ServerControls>
      </TileContent>
    </TileContainer>
  );
};

export default ServerTile;
